import { LoadingSpinner } from "@/component/common/LoadingSpinner";
import { MerchantSessionPayload, SessionPayload } from "@/lib/types";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";

export const VerifyEmail: React.FC<{
  account?: SessionPayload | MerchantSessionPayload;
  refetch: () => void;
}> = ({ account, refetch }) => {
  const {
    data,
    mutate: requestOtp,
    isPending,
  } = useMutation({
    mutationKey: ["requestOtp", account?.id],
    mutationFn: () => {
      return axios
        .post("/api/request-otp", { accountId: account?.id })
        .then((res) => {
          toast.info(
            `We have sent a code to ${account?.email}, please check your email`
          );
          return res.data;
        });
    },
  });

  const [otp, setOtp] = useState("");

  const { mutate: verifyEmail, isPending: isVeryfying } = useMutation({
    mutationKey: ["verifyEmail", account?.id],
    mutationFn: () => {
      return axios
        .post("/api/verify-email", { accountId: account?.id, otp })
        .then((res) => {
          toast.success("Email verified successfully");

          refetch();
          return res.data;
        })
        .catch((_err) => {
          toast.error("Invalid or expired OTP, Please check and try again");
        });
    },
  });

  const [countDown, setCountDown] = useState(0);
  const expiresAt = new Date(data?.resendTime).getTime();

  const isExpired = countDown <= 0;

  useEffect(() => {
    if (expiresAt) {
      setCountDown(
        Number((expiresAt - new Date().getTime()) / 1000).toFixed(
          0
        ) as unknown as number
      );
      const interval = setInterval(() => {
        setCountDown((prev) => {
          if (prev === 0) {
            clearInterval(interval);
            return 0;
          }

          return Math.max(prev - 1, 0);
        });
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [expiresAt]);

  const disableGetCode = isPending || !isExpired;

  return (
    !account?.emailVerified && (
      <div className="fixed bg-slate-900/80 z-50 top-0 left-0 w-full h-full bg-black2 flex items-center justify-center overflow-y-auto">
        <div className="w-4/6 max-lg:w-full max-sm:mx-2 mx-10 justify-content-center mt-2 rounded-lg">
          <div className="eg-login__wrapper  max-sm:pb-32 flex max-sm:grid gap-10 outline outline-fuchsia-400/20">
            <div className="w-1/2 max-sm:w-full px-1 grid min-[600px]:text-center min-[600px]:items-center min-[600px]:justify-center">
              {
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  className="mx-auto size-30 mb-2"
                  src="/assets/img/images/email.png"
                  alt=""
                />
              }
              <span className="text-2xl font-bold mb-2">
                Verify your email to secure your account.
              </span>
            </div>

            <div className="w-1/2 max-sm:w-full px-2 grid justify-stretch items-stretch">
              <span className="text-xl font-bold mb-4">
                Why you need to verify your email
              </span>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex">
                  <CheckBadgeIcon className="size-4 mr-1 inline-block text-appPrimary" />
                  Helps us to send you important transaction notifications.
                </div>
                <div className="flex">
                  <CheckBadgeIcon className="size-4 mr-1 inline-block text-appPrimary" />
                  Helps us to recover your account in case you forget your
                  password.
                </div>
              </div>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="newsletter-form"
              >
                <div className="form-group">
                  <input
                    className="form-control"
                    type="email"
                    placeholder="Enter Code"
                    onChange={(e) => setOtp(e.target.value)}
                    value={otp}
                  />
                </div>

                <button
                  onClick={() => requestOtp()}
                  type="button"
                  className="btn btn5"
                  disabled={disableGetCode}
                >
                  {isPending && <LoadingSpinner />}
                  {countDown ? `Resend in ${countDown}s` : "Get Code"}
                </button>

                <div className="text-sm text-center text-appPrimary">
                  {account?.email}
                </div>
              </form>

              <div className="flex justify-center">
                <button
                  disabled={isPending || !otp || isVeryfying}
                  onClick={() => verifyEmail()}
                  type="button"
                  className="btn btn5 flex justify-between mt-2"
                >
                  {isVeryfying && <LoadingSpinner />}
                  Verify Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
